import React, { useState } from "react";
import { X, UserPlus, Loader2 } from "lucide-react";
import api from "../../connector/api";

const CreateUserModal = ({ isOpen, onClose, onUserCreated }) => {
  const [form, setForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("users/", form);
      onUserCreated(res.data);
      setForm({
        username: "",
        email: "",
        first_name: "",
        last_name: "",
        password: "",
      });
      onClose();
    } catch (err) {
      console.error("Error creating user:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex font-dynamic-title items-center justify-center p-4">
      {/* Backdrop with a smooth blur effect */}
      <div 
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Modal Surface Container */}
      <div className="relative bg-white w-full max-w-md rounded-xl border border-slate-200 shadow-xl p-6 space-y-5 z-10 animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header Elements */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div className="flex items-center gap-2.5">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Register New Profile</h2>
              <p className="text-[11px] text-slate-400 mt-0.5">Initialize a new corporate access control ledger.</p>
            </div>
          </div>
          <button 
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Inputs Element */}
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Dual Column for First / Last Names to reduce modal height footprint */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">First Name</label>
              <input
                type="text"
                name="first_name"
                placeholder="e.g. John"
                value={form.first_name}
                onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-2xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Last Name</label>
              <input
                type="text"
                name="last_name"
                placeholder="e.g. Doe"
                value={form.last_name}
                onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-2xs"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Username <span className="text-rose-500">*</span></label>
            <input
              type="text"
              name="username"
              placeholder="johndoe"
              value={form.username}
              onChange={handleChange}
              className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-2xs"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Email Address <span className="text-rose-500">*</span></label>
            <input
              type="email"
              name="email"
              placeholder="name@corporate.com"
              value={form.email}
              onChange={handleChange}
              className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-2xs"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Security Password <span className="text-rose-500">*</span></label>
            <input
              type="password"
              name="password"
              placeholder="••••••••••••"
              value={form.password}
              onChange={handleChange}
              className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-2xs"
              required
            />
          </div>

          {/* Action Row */}
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 rounded-xl transition-all shadow-2xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 text-xs font-semibold text-white rounded-xl transition-all shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Generating Profile...
                </>
              ) : (
                "Commit Profile"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;